import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Clock, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Container, Eyebrow } from "@/components/common/Section";
import Reveal from "@/components/common/Reveal";
import { FieldText, FieldTextarea, isEmail } from "@/components/common/FormField";
import { submitContact } from "@/lib/apiClient";
import { INTEREST_OPTIONS, slugToInterest } from "@/data/solutions";

const initial = { name: "", email: "", organization: "", subject: "", interest: "", message: "" };

const highlights = [
  { icon: Clock, title: "Fast response", desc: "We typically reply within one business day." },
  { icon: ShieldCheck, title: "Secure & private", desc: "Your details are stored securely and never sold." },
  { icon: Mail, title: "Direct to our team", desc: "Reach engineering, sales, or support in one place." },
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => ({
    ...initial,
    interest: slugToInterest(searchParams.get("interest")),
  }));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!isEmail(form.email)) e.email = "Enter a valid email address.";
    if (!form.message.trim()) e.message = "Please enter a message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim() || null,
        subject: form.subject.trim() || null,
        interest: form.interest || null,
        message: form.message.trim(),
      });
      toast.success(res.message || "Message sent!", { className: "" });
      setDone(true);
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="bg-sentira-grid bg-radial-fade pointer-events-none absolute inset-0 opacity-60" />
        <Container className="relative py-20 md:py-24">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <Reveal>
                <Eyebrow>Contact Sentira</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-5 text-4xl font-semibold leading-[1.08] tracking-[-0.02em] sm:text-5xl">
                  Let's talk about your operations
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
                  Questions about Licara, custom development, or partnerships?
                  Send us a note and the right person will get back to you.
                </p>
              </Reveal>
              <div className="mt-10 space-y-5">
                {highlights.map((h, i) => (
                  <Reveal key={h.title} delay={0.12 + i * 0.06}>
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-border bg-secondary">
                        <h.icon className="h-4.5 w-4.5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{h.title}</p>
                        <p className="text-sm text-muted-foreground">{h.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <Reveal delay={0.1} y={20}>
              <div className="rounded-[var(--radius-card)] border border-border bg-card p-6 shadow-[var(--shadow-card)] md:p-8">
                {done ? (
                  <div className="flex flex-col items-center py-10 text-center" data-testid="contact-success-state">
                    <CheckCircle2 className="h-12 w-12 text-primary" />
                    <h3 className="mt-4 text-xl font-semibold">Message received</h3>
                    <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                      Thanks for reaching out to Sentira. Our team will respond
                      shortly.
                    </p>
                    <Button
                      variant="secondary"
                      className="mt-6"
                      onClick={() => setDone(false)}
                      data-testid="contact-send-another-button"
                    >
                      Send another message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <FieldText id="c-name" label="Full name" required value={form.name} onChange={set("name")} error={errors.name} testId="contact-form-name-input" placeholder="Jane Doe" />
                      <FieldText id="c-email" label="Work email" required type="email" value={form.email} onChange={set("email")} error={errors.email} testId="contact-form-email-input" placeholder="jane@agency.gov" />
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <FieldText id="c-org" label="Organization" value={form.organization} onChange={set("organization")} error={errors.organization} testId="contact-form-organization-input" placeholder="City of Springfield" />
                      <FieldText id="c-subject" label="Subject" value={form.subject} onChange={set("subject")} error={errors.subject} testId="contact-form-subject-input" placeholder="Product inquiry" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">What are you interested in?</Label>
                      <Select value={form.interest} onValueChange={set("interest")}>
                        <SelectTrigger data-testid="contact-form-interest-select" className="bg-[hsl(220_30%_8%)] border-border">
                          <SelectValue placeholder="Select a solution" />
                        </SelectTrigger>
                        <SelectContent>
                          {INTEREST_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <FieldTextarea id="c-message" label="Message" required value={form.message} onChange={set("message")} error={errors.message} testId="contact-form-message-input" placeholder="Tell us a bit about what you're looking for..." />
                    <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]" data-testid="contact-form-submit-button">
                      {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>) : "Send message"}
                    </Button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
    </div>
  );
}
